import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import {
  ALLOWED_QUERY_VALUES,
  CAMPAIGN_TAG_VALUE,
  CONSENT_DEFAULTS,
  GA_CONFIG,
  GTAG_SCRIPT_URL,
  MEASUREMENT_ID,
  NOT_FOUND_PAGE_KEY,
  UTM_PARAMS,
} from '../data/analytics.config';
import { APP_URLS, DEMO_BOOKING_URL, ONBOARDING } from '../data/site.config';
import type {
  ContactErrorKind,
  CtaReport,
  FaqReport,
  LegalOutcome,
  ResolvedPath,
  TrackedPage,
} from '../models/analytics.model';
import type { BillingTerm } from '../models/pricing.model';
import type { ProductSlug } from '../models/product.model';
import {
  contentGroupOf,
  destinationKeyOf,
  pathnameOf,
  queryOf,
  resolvePath,
} from '../utils/analytics-path';
import { canonicalPath, langOfPath, localePath } from '../utils/locale-path';
import { ConsentService } from './consent.service';
import { LanguageService } from './language.service';

/** A previous location as the next page's referrer: campaign tags are sent once, not twice. */
function withoutCampaignTags(location: string): string {
  const url = new URL(location);
  for (const name of UTM_PARAMS) url.searchParams.delete(name);
  return url.toString();
}

/** A host's registrable parent, its last two labels (`.avyna.io`); none for localhost or an IP. */
function parentCookieDomain(host: string): string | null {
  const labels = host.split('.');
  const isIp = host.includes(':') || /^\d+(\.\d+){3}$/.test(host);
  if (isIp || labels.length < 2 || labels.at(-1) === 'localhost') return null;
  return `.${labels.slice(-2).join('.')}`;
}

/**
 * The only code that talks to Google Analytics, exactly as docs/documentation/analytics.md
 * plans it. Components report through its typed methods and never touch `gtag`.
 *
 * Nothing is requested before consent. On acceptance it injects `gtag.js` once; on refusal,
 * withdrawal or lapse it disables the tag and deletes GA's cookies on this host and on its
 * parent domain, as it does at start-up whenever no grant is in effect.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly consent = inject(ConsentService);
  private readonly language = inject(LanguageService);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private started = false;
  private scriptLoaded = false;
  private sending = false;

  private page: TrackedPage | null = null;
  private lastPath: string | null = null;
  private entryUrl: string | null = null;
  /** A legal page waits for `LegalDocument` to say how it ended before it counts as a page. */
  private pendingLegal: { readonly url: string; readonly resolved: ResolvedPath } | null = null;
  private readonly legalOutcomes = new Map<string, LegalOutcome>();

  /** Called once by an app initializer; does nothing on the server or without a measurement ID. */
  start(): void {
    if (this.started || !this.isBrowser || !this.consent.enabled) return;
    this.started = true;

    // No grant in effect (no answer yet, a refusal or a lapse): no GA cookie may outlive it.
    if (!this.consent.granted()) this.deleteGaCookies();
    this.followRouter();
    this.consent.changes.subscribe((change) => (change === 'granted' ? this.grant() : this.deny()));
    if (this.consent.granted()) this.grant();
  }

  ctaClicked(report: CtaReport): void {
    if (report.href === ONBOARDING.catalog) {
      this.send('onboarding_start', {
        product: 'catalog',
        cta_id: report.id,
        cta_location: report.location,
        plan_code: report.planCode,
        billing_term: report.planCode ? report.billingTerm : undefined,
      });
      return;
    }

    if (report.href === DEMO_BOOKING_URL) {
      this.send('demo_booking_start', {
        product: 'catalog',
        cta_id: report.id,
        cta_location: report.location,
      });
      return;
    }

    if (!report.href.startsWith('/')) return;
    this.send('cta_click', {
      cta_id: report.id,
      cta_location: report.location,
      destination: destinationKeyOf(report.href),
      plan_code: report.planCode,
    });
  }

  /** A footer link to a product's own application. */
  signInClicked(href: string): void {
    const product = (Object.keys(APP_URLS) as ProductSlug[]).find((slug) => APP_URLS[slug] === href);
    if (product) this.send('sign_in_click', { product });
  }

  leadSent(entryParam: string | undefined): void {
    this.send('generate_lead', { lead_source: 'contact_form', entry_param: entryParam });
  }

  contactFailed(kind: ContactErrorKind, invalidFields: readonly string[] = []): void {
    this.send('contact_error', {
      kind,
      invalid_fields: invalidFields.length ? invalidFields.join(',') : undefined,
    });
  }

  mailtoFallbackClicked(): void {
    this.send('contact_mailto_fallback', {});
  }

  billingTermChanged(term: BillingTerm): void {
    this.send('billing_term_change', { billing_term: term });
  }

  faqOpened(report: FaqReport): void {
    this.send('faq_open', {
      faq_location: report.location,
      question_index: report.index,
      product: report.product,
      topic_key: report.topicKey,
    });
  }

  screenshotZoomed(name: string, product: ProductSlug): void {
    this.send('screenshot_zoom', { screenshot_name: name, product });
  }

  legalDownloaded(doc: string, product: ProductSlug): void {
    this.send('legal_download', { legal_doc: doc, product });
  }

  privacyOpened(): void {
    this.send('privacy_open', {});
  }

  languageChanged(from: string, to: string): void {
    this.send('language_change', { from, to });
  }

  /** `LegalDocument` reports how its page ended, which the Router cannot know. */
  legalPageSettled(product: ProductSlug, doc: string, outcome: LegalOutcome): void {
    const path = `/${product}/legal/${doc}`;
    this.legalOutcomes.set(path, outcome);

    const pending = this.pendingLegal;
    if (pending && canonicalPath(pathnameOf(pending.url)) === path) this.settleLegal(outcome);
  }

  private followRouter(): void {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) this.legalOutcomes.clear();
      if (event instanceof NavigationEnd) this.onPathVisited(event.urlAfterRedirects);
    });
  }

  private onPathVisited(url: string): void {
    const atEntry = this.entryUrl === null;
    if (atEntry) {
      this.entryUrl = url;
      if (this.language.entryRedirectTarget(url)) return;
    }

    const path = pathnameOf(url);
    if (path === this.lastPath) return;
    this.lastPath = path;
    this.pendingLegal = null;

    const resolved = resolvePath(url);
    if (resolved.kind === 'unlisted-guide') return;
    if (resolved.kind === 'legal') {
      this.pendingLegal = { url, resolved };
      const known = this.legalOutcomes.get(canonicalPath(path));
      if (known) this.settleLegal(known);
      else this.setPageFields(this.describe(url, resolved));
      return;
    }

    this.show(url, resolved);
  }

  private settleLegal(outcome: LegalOutcome): void {
    const pending = this.pendingLegal;
    if (!pending || pending.resolved.kind !== 'legal') return;
    this.pendingLegal = null;

    if (outcome === 'not-found') {
      this.show(pending.url, { kind: 'not-found' });
      return;
    }
    this.show(pending.url, pending.resolved, outcome === 'resolved' ? pending.resolved.doc : undefined);
  }

  private show(url: string, resolved: ResolvedPath, legalDoc?: string): void {
    this.page = this.describe(url, resolved, legalDoc);
    this.sendPageView();
  }

  private describe(url: string, resolved: ResolvedPath, legalDoc?: string): TrackedPage {
    const path = pathnameOf(url);
    const lang = langOfPath(path);
    const found = resolved.kind === 'page' || resolved.kind === 'legal';
    const pageKey = found ? resolved.pageKey : NOT_FOUND_PAGE_KEY;
    const visited = found ? path : localePath(NOT_FOUND_PAGE_KEY, lang);
    const previous = this.page;

    return {
      pageKey,
      lang,
      location: `${environment.siteUrl}${visited}${this.allowedQuery(url, previous === null)}`,
      referrer: previous ? withoutCampaignTags(previous.location) : this.entryReferrer(),
      contentGroup: contentGroupOf(pageKey),
      guideSlug: resolved.kind === 'page' ? resolved.guideSlug : undefined,
      legalDoc,
      fromPageKey: previous?.pageKey ?? 'entry',
    };
  }

  /** Site-generated values only; campaign tags only on the visit's first page, read from the entry address. */
  private allowedQuery(url: string, firstPage: boolean): string {
    const kept = new URLSearchParams();
    const query = queryOf(url);

    for (const [name, values] of Object.entries(ALLOWED_QUERY_VALUES)) {
      const value = query.get(name);
      if (value !== null && values.includes(value)) kept.set(name, value);
    }

    if (firstPage && this.entryUrl) {
      const entry = queryOf(this.entryUrl);
      for (const name of UTM_PARAMS) {
        const value = entry.get(name);
        if (value && CAMPAIGN_TAG_VALUE.test(value)) kept.set(name, value);
      }
    }

    const search = kept.toString();
    return search ? `?${search}` : '';
  }

  private entryReferrer(): string {
    try {
      return this.document.referrer ? new URL(this.document.referrer).origin : '';
    } catch {
      return '';
    }
  }

  /** Every hit, automatic ones included, reads these rather than the raw address and title. */
  private setPageFields(page: TrackedPage): void {
    if (!this.sending) return;

    this.gtag('set', {
      page_location: page.location,
      page_title: page.pageKey,
      page_referrer: page.referrer,
      content_group: page.contentGroup,
    });
  }

  private sendPageView(): void {
    const page = this.page;
    if (!this.sending || !page) return;

    this.setPageFields(page);
    this.send('page_view', {
      page_key: page.pageKey,
      page_lang: page.lang,
      guide_slug: page.guideSlug,
      legal_doc: page.legalDoc,
    });
    if (page.pageKey === NOT_FOUND_PAGE_KEY) {
      this.send('page_not_found', { from_page_key: page.fromPageKey });
    }
  }

  private grant(): void {
    if (this.scriptLoaded) {
      this.setDisabled(false);
      this.gtag('consent', 'update', { analytics_storage: 'granted' });
    } else {
      this.loadScript();
    }

    this.sending = true;
    // A legal page in view has not settled yet; settling sends its page_view.
    const pending = this.pendingLegal;
    if (pending) this.setPageFields(this.describe(pending.url, pending.resolved));
    else this.sendPageView();
  }

  private deny(): void {
    this.sending = false;
    if (this.scriptLoaded) {
      this.gtag('consent', 'update', { analytics_storage: 'denied' });
      this.setDisabled(true);
    }
    this.deleteGaCookies();
  }

  private loadScript(): void {
    const win = this.document.defaultView!;
    win.dataLayer = win.dataLayer ?? [];
    win.gtag = function gtag() {
      // gtag.js reads the Arguments object itself, not an array copy of it.
      win.dataLayer!.push(arguments);
    };

    // Defaults first, then the visitor's grant, then config: if config ran while analytics_storage
    // was still denied, gtag would start on a temporary client ID and the first hit would count
    // as a different visitor from every hit after it.
    this.gtag('consent', 'default', CONSENT_DEFAULTS);
    this.gtag('consent', 'update', { analytics_storage: 'granted' });

    const script = this.document.createElement('script');
    script.async = true;
    script.src = `${GTAG_SCRIPT_URL}?id=${encodeURIComponent(MEASUREMENT_ID)}`;
    this.document.head.appendChild(script);

    this.gtag('js', new Date());
    this.gtag('config', MEASUREMENT_ID, GA_CONFIG);
    this.scriptLoaded = true;
  }

  private setDisabled(disabled: boolean): void {
    (this.document.defaultView as unknown as Record<string, unknown>)[`ga-disable-${MEASUREMENT_ID}`] =
      disabled;
  }

  /**
   * `_ga` and the stream's own `_ga_<id>`, host-only since `cookie_domain` is `none`. Each is
   * also expired on the parent domain (`.avyna.io`), in case anything ever wrote it there.
   */
  private deleteGaCookies(): void {
    const stream = `_ga_${MEASUREMENT_ID.replace(/^G-/, '')}`;
    const parent = parentCookieDomain(new URL(environment.siteUrl).hostname);
    for (const name of ['_ga', stream]) {
      this.document.cookie = `${name}=; Max-Age=0; path=/`;
      if (parent) this.document.cookie = `${name}=; Max-Age=0; path=/; domain=${parent}`;
    }
  }

  private send(name: string, params: Record<string, string | number | undefined>): void {
    if (!this.sending) return;

    // gtag('set') carries the page fields to every hit, but not content_group: each event takes it itself.
    const withGroup = { content_group: this.page?.contentGroup, ...params };
    const defined = Object.fromEntries(
      Object.entries(withGroup).filter(([, value]) => value !== undefined),
    );
    this.gtag('event', name, defined);
  }

  private gtag(...args: unknown[]): void {
    this.document.defaultView?.gtag?.(...args);
  }
}
